import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Copy, ExternalLink, LogOut, Loader2, AlertTriangle, RefreshCw, Tag,
  TrendingUp, MousePointerClick, ShoppingBag, DollarSign, Save, Hourglass,
  CheckCircle2, XCircle, Percent,
} from 'lucide-react';
import { AffiliateHeader, AffiliateFooter } from '../../components/affiliate/AffiliateChrome';
import {
  affiliateApi, getAuthToken, clearAuthToken,
  buildAffiliatePath, buildAffiliateUrl,
} from '../../lib/affiliate';

const Stat = ({ icon: Icon, label, value, suffix }) => (
  <div className="p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800">
    <div className="flex items-center gap-2 text-xs text-neutral-400 uppercase tracking-wider font-semibold">
      <Icon className="w-4 h-4 text-axon-500" />
      {label}
    </div>
    <div className="mt-2 text-3xl font-bold tracking-tight">
      {value}
      {suffix && <span className="text-base text-neutral-400 font-medium ml-1">{suffix}</span>}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    pending:  { icon: Hourglass,    cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30', text: 'Pending review' },
    approved: { icon: CheckCircle2, cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', text: 'Approved' },
    rejected: { icon: XCircle,      cls: 'bg-red-500/10 text-red-300 border-red-500/30', text: 'Not approved' },
  };
  const conf = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${conf.cls}`}>
      <conf.icon className="w-3.5 h-3.5" />
      {conf.text}
    </span>
  );
};

const AffiliateDashboardPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState({ clicks: 0, conversions: 0, revenue: 0, total_orders: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [discount, setDiscount] = useState(0);
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const data = await affiliateApi('/affiliates/me');
      setMe(data.affiliate);
      setStats(data.stats || stats);
      setRecent(data.recent_orders || []);
      setDiscount(Number(data.affiliate?.discount_pct || 0));
    } catch (e) {
      if (e.status === 401) {
        clearAuthToken();
        navigate(buildAffiliatePath('/login'));
        return;
      }
      setErr(e.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getAuthToken()) {
      navigate(buildAffiliatePath('/login'));
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = () => {
    clearAuthToken();
    navigate(buildAffiliatePath('/login'));
  };

  const link = me ? buildAffiliateUrl(me.subdomain) : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const onSaveDiscount = async () => {
    setSavingDiscount(true);
    setErr('');
    try {
      const data = await affiliateApi('/affiliates/me/discount', {
        method: 'PATCH',
        body: JSON.stringify({ discount_pct: Number(discount) }),
      });
      setMe(data.affiliate);
    } catch (e) {
      setErr(e.message || 'Could not update discount.');
    } finally {
      setSavingDiscount(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <AffiliateHeader />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex items-center gap-3 text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading dashboard…</div>
        ) : !me ? (
          <div className="text-neutral-400">No data.</div>
        ) : (
          <>
            {/* Header row */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-axon-500 font-semibold">Affiliate dashboard</p>
                <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                  Welcome, {me.first_name}
                </h1>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <StatusPill status={me.status} />
                  <span className="text-xs text-neutral-500">Commission: <span className="text-white font-semibold">{me.commission_pct}%</span></span>
                  <span className="text-xs text-neutral-500">Descriptor: <span className="font-mono text-axon-400">{me.descriptor}</span></span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral-700 hover:border-axon-500 text-sm font-semibold uppercase tracking-wider transition-colors" data-testid="refresh-dashboard">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral-700 hover:border-red-500 text-sm font-semibold uppercase tracking-wider transition-colors" data-testid="logout">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </div>

            {err && (
              <div className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            {/* Status notices */}
            {me.status === 'pending' && (
              <div className="mb-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm">
                Your application is being reviewed. Once approved, your link below will go live and start tracking clicks and orders.
              </div>
            )}
            {me.status === 'rejected' && (
              <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
                Your application was not approved. Please contact our team for more information.
              </div>
            )}

            {/* Affiliate link */}
            <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-axon-700/30 via-axon-600/20 to-neutral-900/60 border border-axon-700/40">
              <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-widest text-axon-300">
                <Tag className="w-3.5 h-3.5" /> Your affiliate link
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <code className="flex-1 px-4 py-3 rounded-xl bg-neutral-950 border border-neutral-800 font-mono text-sm break-all" data-testid="affiliate-link">
                  {link}
                </code>
                <div className="flex gap-2">
                  <button onClick={copyLink} className="px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-700 hover:border-axon-500 text-sm font-semibold uppercase tracking-wider transition-colors inline-flex items-center gap-2" data-testid="copy-link">
                    <Copy className="w-4 h-4" /> {copied ? 'Copied' : 'Copy'}
                  </button>
                  <a href={link} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white text-sm font-semibold uppercase tracking-wider transition-all inline-flex items-center gap-2 shadow-button hover:shadow-glow-cyan" data-testid="open-link">
                    <ExternalLink className="w-4 h-4" /> Open
                  </a>
                </div>
              </div>
              {me.status !== 'approved' && (
                <p className="mt-3 text-xs text-amber-300/90">
                  Heads up: visitors won&apos;t be tracked or attributed to you until your account is approved.
                </p>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Stat icon={MousePointerClick} label="Clicks"      value={stats.clicks} />
              <Stat icon={ShoppingBag}       label="Conversions" value={stats.conversions} />
              <Stat icon={DollarSign}        label="Revenue"     value={`$${(stats.revenue || 0).toFixed(2)}`} />
              <Stat icon={TrendingUp}        label="Conv. rate"  value={stats.clicks ? ((stats.conversions / stats.clicks) * 100).toFixed(1) : '0'} suffix="%" />
            </div>

            {/* Discount + recent orders */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-1 p-6 rounded-2xl bg-neutral-900/60 border border-neutral-800">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold uppercase tracking-wider">
                  <Percent className="w-4 h-4 text-axon-500" /> Customer discount (optional)
                </div>
                <p className="text-xs text-neutral-400 mb-4">
                  Offer a percent-off discount that automatically applies on your store. Set 0 to disable.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={0} max={50} step={1}
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    disabled={me.status !== 'approved'}
                    className="w-24 bg-neutral-950 px-4 py-3 rounded-xl border border-neutral-700 focus:border-axon-500 outline-none text-center font-bold disabled:opacity-50"
                    data-testid="discount-input"
                  />
                  <span className="text-neutral-400">%</span>
                  <button
                    onClick={onSaveDiscount}
                    disabled={savingDiscount || me.status !== 'approved'}
                    className="ml-auto inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider transition-all"
                    data-testid="save-discount"
                  >
                    {savingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
                  </button>
                </div>
              </div>

              <div className="lg:col-span-2 p-6 rounded-2xl bg-neutral-900/60 border border-neutral-800">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">Recent orders</h3>
                {recent.length === 0 ? (
                  <p className="text-sm text-neutral-500">No orders yet. Share your link to start earning.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs uppercase tracking-wider text-neutral-500 text-left">
                          <th className="py-2 pr-4">Order</th>
                          <th className="py-2 pr-4">Customer</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4 text-right">Amount</th>
                          <th className="py-2 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((o) => (
                          <tr key={o.order_id} className="border-t border-neutral-800">
                            <td className="py-3 pr-4 font-mono text-xs">{o.order_id}</td>
                            <td className="py-3 pr-4">{o.customer_email || '—'}</td>
                            <td className="py-3 pr-4 capitalize">{o.status}</td>
                            <td className="py-3 pr-4 text-right">${Number(o.amount || 0).toFixed(2)}</td>
                            <td className="py-3 text-right text-neutral-400 text-xs">{(o.created_at || '').slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
      <AffiliateFooter />
    </div>
  );
};

export default AffiliateDashboardPage;
